import { Component, Input, Output, EventEmitter, OnInit } from '@angular/core';
import { AllSameKeyValuesEnforcer, AllSameKeysEnforcerWithValue } from 'src/app/helpers/helpers';
import { AccountEventStates, accountEventStates } from 'src/app/store/reselectors/null.reselector';
import publicHelpers from 'globals/helpers/public-helpers';
import { TraceNameType } from 'src/app/services/performance.tracking.data';
import { LoaderPaneFragmentComponent } from 'src/app/components/loader/loader-pane-fragment.component';
import { LoaderPaneComponent } from 'src/app/components/loader/loader-pane.component';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';

@Component({
    selector: 'null-state',
    imports: [LoaderPaneFragmentComponent, CommonModule, MatButtonModule],
    templateUrl: './abnormal_state.html',
    styleUrls: [`./abnormal_state.sass`],
    standalone: true,
})

export class AbnormalStateComponent implements OnInit {
  @Input() state: keyof AccountEventStates
  @Input() title: string
  @Input() traceName: TraceNameType
  @Input() background = '#eef2f5'
  @Input() hideButton = false
  @Input() inputData: NullStateData
  publicHelpers = publicHelpers

  @Output() buttonClick: EventEmitter<void> = new EventEmitter();
  data: NullStateData = null

  ngOnInit() {
    this.data = this.inputData || stateData[this.state]
  }

}

interface StateImages { welcome, blueBox, pinkBox, timeline, notey, purpleWoman }
export const nullStateImages: AllSameKeyValuesEnforcer<StateImages> = { welcome: 'welcome', blueBox: 'blueBox', pinkBox: 'pinkBox', timeline: 'timeline', notey: 'notey', purpleWoman: 'purpleWoman' }


export interface NullStateData {
  title?: string
  message?: string
  buttonText?: string
  imageClass?: string
  postMessage?: string
}


const noResults: NullStateData = {
  title: "No results!",
  message: "Try adjusting your search or filter.",
  buttonText: "",
  imageClass: nullStateImages.blueBox,
}
const nothingHere: NullStateData = {
  message: 'Click the <em>New Event</em> button to get started.',
  title: 'It’s time to create an event!',
  imageClass: nullStateImages.purpleWoman
}

const noSubscription: NullStateData = {
  message: "Before you start building your events, you need an active subscription!",
  buttonText: "SUBSCRIBE NOW",
  imageClass: nullStateImages.pinkBox,
}

const stateData: AllSameKeysEnforcerWithValue<AccountEventStates, NullStateData> = {
  [accountEventStates.singleSuspended]: {
    message: 'This account is inactive',
    buttonText: 'Please click here to reactivate',
    imageClass: nullStateImages.pinkBox,
  },
  [accountEventStates.notShared]: {
    message: "Here you can publish your schedule timeline to a shareable URL. You can even create multiple timelines with different filters for different audiences!",
    buttonText: "ADD A TIMELINE",
    imageClass: nullStateImages.timeline,
  },
  [accountEventStates.logisticsNotShared]: {
    message: "Here you can publish your production view to a shareable URL. You can even create multiple production views with different filters for different audiences!",
    buttonText: "ADD A PRODUCTION VIEW",
    imageClass: nullStateImages.timeline,
  },
  [accountEventStates.notPublished]: {
    message: "Here you can publish your program page to a shareable URL. You can even create multiple published pages with different filters for different audiences!",
    buttonText: "PUBLISH A PAGE",
    imageClass: nullStateImages.timeline,
  },
  [accountEventStates.loading]: {}, // these "{}" states are not for display, but carry important information for other uses
  [accountEventStates.normal]: {},
  [accountEventStates.suspended]: {},
  [accountEventStates.noSubscriptionWithEvents]: {},
  [accountEventStates.noSubscriptionInvitationsOnly]: {},
  [accountEventStates.noResults]: noResults,
  [accountEventStates.noSubscriptionWithEventsButFilteredOut]: noResults,
  [accountEventStates.noEvents]: nothingHere,
  [accountEventStates.invitationsOnly]: nothingHere,
  [accountEventStates.noSubscription]: noSubscription,
  [accountEventStates.noSubscriptionWithEventsButClosedAndInvisible]: {
    message: "Before you make new events, you need an active subscription! (or click on 'Show closed events' to view closed events)",
    buttonText: "SUBSCRIBE NOW",
    imageClass: nullStateImages.pinkBox,
  },
  [accountEventStates.hiddenClosedEventsOnly]: {
    message: 'Click the <em>New Event</em> button to get started.',
    title: 'Nothing here!',
    imageClass: nullStateImages.blueBox
  },
  [accountEventStates.fresh]: {
    message: 'Welcome to Joi! Let\'s kick things off by creating a new event.',
    buttonText: 'NEW EVENT',
    imageClass: nullStateImages.welcome
  },
  [accountEventStates.noAccounts]: {
    message: "Before you start building your events, you need to make an account to put them in!",
    buttonText: "NEW ACCOUNT",
    imageClass: nullStateImages.pinkBox,
  },
  [accountEventStates.emailUnverified]: {
    message: "Joi now requires email verification to continue.",
    buttonText: "SEND VERIFICATION EMAIL",
    imageClass: nullStateImages.purpleWoman,
  },
}